"use client";

import type { ComponentProps } from "react";
import { useEffect, useRef } from "react";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/20/solid";
import {
  DayPicker,
  getDefaultClassNames,
  type DayButton,
  type Locale,
} from "react-day-picker";
import { cn } from "@/lib/cn";

const navButton =
  "inline-flex size-(--cell-size) cursor-pointer items-center justify-center rounded-small p-0 text-neutral-black select-none hover:bg-neutral-black/5 aria-disabled:pointer-events-none aria-disabled:opacity-30";

export const Calendar = ({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "dropdown",
  locale,
  formatters,
  components,
  ...props
}: ComponentProps<typeof DayPicker>) => {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("group/calendar [--cell-size:40px]", className)}
      captionLayout={captionLayout}
      locale={locale}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString(locale?.code, { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit font-sans text-neutral-black", defaultClassNames.root),
        months: cn(
          "relative flex flex-col gap-16 md:flex-row",
          defaultClassNames.months,
        ),
        month: cn("flex w-full flex-col gap-16", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-4",
          defaultClassNames.nav,
        ),
        button_previous: cn(navButton, defaultClassNames.button_previous),
        button_next: cn(navButton, defaultClassNames.button_next),
        month_caption: cn(
          "flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)",
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          "flex h-(--cell-size) w-full items-center justify-center gap-6 text-14 font-semibold",
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          "relative rounded-small border border-neutral-light-grey hover:bg-neutral-black/5 has-focus:border-brand-blue data-disabled:opacity-50",
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn(
          "absolute inset-0 cursor-pointer bg-neutral-white opacity-0",
          defaultClassNames.dropdown,
        ),
        caption_label: cn(
          "font-semibold text-neutral-black select-none",
          captionLayout === "label"
            ? "text-16"
            : "flex items-center gap-4 rounded-small px-8 text-14 [&>svg]:size-14 [&>svg]:text-neutral-grey",
          defaultClassNames.caption_label,
        ),
        month_grid: cn("w-full border-collapse", defaultClassNames.month_grid),
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "flex-1 text-12 font-normal text-neutral-grey select-none",
          defaultClassNames.weekday,
        ),
        week: cn("mt-8 flex w-full", defaultClassNames.week),
        week_number_header: cn(
          "w-(--cell-size) select-none",
          defaultClassNames.week_number_header,
        ),
        week_number: cn(
          "text-12 text-neutral-grey select-none",
          defaultClassNames.week_number,
        ),
        day: cn(
          "group/day relative aspect-square h-full w-full p-0 text-center select-none [&:last-child[data-selected=true]_button]:rounded-r-small",
          props.showWeekNumber
            ? "[&:nth-child(2)[data-selected=true]_button]:rounded-l-small"
            : "[&:first-child[data-selected=true]_button]:rounded-l-small",
          defaultClassNames.day,
        ),
        range_start: cn(
          "rounded-l-small bg-tone-aqua-tint",
          defaultClassNames.range_start,
        ),
        range_middle: cn(
          "rounded-none bg-tone-aqua-tint",
          defaultClassNames.range_middle,
        ),
        range_end: cn(
          "rounded-r-small bg-tone-aqua-tint",
          defaultClassNames.range_end,
        ),
        today: cn(
          "rounded-small bg-neutral-light-grey data-[selected=true]:rounded-none",
          defaultClassNames.today,
        ),
        outside: cn(
          "text-neutral-mid-grey aria-selected:text-neutral-mid-grey",
          defaultClassNames.outside,
        ),
        disabled: cn(
          "text-neutral-mid-grey opacity-50",
          defaultClassNames.disabled,
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...rootProps }) => (
          <div
            data-slot="calendar"
            ref={rootRef}
            className={cn(className)}
            {...rootProps}
          />
        ),
        Chevron: ({ className, orientation, ...chevronProps }) => {
          const Icon =
            orientation === "left"
              ? ChevronLeftIcon
              : orientation === "right"
                ? ChevronRightIcon
                : ChevronDownIcon;

          return (
            <Icon className={cn("size-16", className)} {...chevronProps} />
          );
        },
        DayButton: (dayButtonProps) => (
          <CalendarDayButton locale={locale} {...dayButtonProps} />
        ),
        WeekNumber: ({ children, ...weekNumberProps }) => (
          <td {...weekNumberProps}>
            <div className="flex size-(--cell-size) items-center justify-center text-center">
              {children}
            </div>
          </td>
        ),
        ...components,
      }}
      {...props}
    />
  );
};

export const CalendarDayButton = ({
  className,
  day,
  modifiers,
  locale,
  ...props
}: ComponentProps<typeof DayButton> & { locale?: Partial<Locale> }) => {
  const defaultClassNames = getDefaultClassNames();
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <button
      ref={ref}
      type="button"
      data-day={day.date.toLocaleDateString(locale?.code)}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        "relative flex aspect-square w-full min-w-(--cell-size) cursor-pointer flex-col items-center justify-center gap-4 rounded-small border-0 text-14 leading-none font-normal transition-colors hover:bg-neutral-black/5 disabled:pointer-events-none",
        "group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-2 group-data-[focused=true]/day:ring-brand-blue",
        "data-[selected-single=true]:bg-brand-blue data-[selected-single=true]:text-neutral-white data-[selected-single=true]:hover:bg-brand-blue",
        "data-[range-start=true]:rounded-l-small data-[range-start=true]:bg-brand-blue data-[range-start=true]:text-neutral-white",
        "data-[range-end=true]:rounded-r-small data-[range-end=true]:bg-brand-blue data-[range-end=true]:text-neutral-white",
        "data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-transparent data-[range-middle=true]:text-neutral-black",
        "[&>span]:text-12 [&>span]:opacity-70",
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  );
};
